import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, Sparkles, Search, ShoppingBag, GraduationCap, MessageSquare, ShieldCheck, Trash2,
  Loader2, ArrowRight,
} from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { formatPrice, formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

function Section({ icon: Icon, title, children, count }) {
  return (
    <div className="rounded-2xl border border-line bg-white p-5">
      <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-extrabold text-ink">
        <Icon className="h-4 w-4 text-violet-600" /> {title}
        {count != null && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-black text-violet-700">{count}</span>}
      </h3>
      {children}
    </div>
  );
}

function MiniCard({ card }) {
  return (
    <Link to={card.url} className="flex items-center gap-2 rounded-xl border border-line p-2 transition-colors hover:border-violet-300">
      <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-surface">
        {card.image_url ? <img src={fileUrl(card.image_url)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-violet-400"><Sparkles className="h-4 w-4" /></div>}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12.5px] font-semibold text-ink">{card.title}</p>
        <p className="text-[11px] font-bold text-violet-700">{formatPrice(card.price)}</p>
      </div>
      <ArrowRight className="h-4 w-4 text-slate-300" />
    </Link>
  );
}

const PRIVACY_ITEMS = [
  { key: "activity_tracking", label: "Suivi d'activité", desc: "Enregistrer produits/formations consultés pour améliorer le service et l'assistance." },
  { key: "personalization", label: "Recommandations personnalisées", desc: "Utiliser mes interactions pour me proposer des suggestions pertinentes." },
  { key: "ai_notifications", label: "Notifications intelligentes", desc: "Recevoir des messages utiles (commandes, formations, promotions)." },
];

export default function AccountActivity() {
  const [data, setData] = useState(null);
  const [recos, setRecos] = useState([]);
  const [privacy, setPrivacy] = useState({ activity_tracking: true, personalization: true, ai_notifications: true });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: act }, { data: rec }] = await Promise.all([
        api.get("/ai/my-activity"),
        api.get("/ai/recommendations"),
      ]);
      setData(act);
      setPrivacy(act.privacy || privacy);
      setRecos(rec.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const togglePrivacy = async (key) => {
    const next = { ...privacy, [key]: !privacy[key] };
    setPrivacy(next);
    try {
      await api.put("/ai/privacy", { [key]: next[key] });
      toast.success("Préférence enregistrée");
    } catch (e) {
      toast.error(apiError(e));
      setPrivacy(privacy);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Effacer tout votre historique d'activité et vos conversations avec ISMO-TECH AI ?")) return;
    try {
      await api.delete("/ai/my-activity");
      toast.success("Votre historique a été effacé");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>;

  return (
    <div className="space-y-6" data-testid="account-activity">
      <div className="flex items-center gap-3 rounded-3xl bg-gradient-to-br from-violet-600 to-indigo-700 p-6 text-white">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15"><Activity className="h-6 w-6" /></span>
        <div>
          <h1 className="font-display text-xl font-black sm:text-2xl">Mon activité</h1>
          <p className="text-sm text-white/80">Vos consultations, recherches et conversations avec ISMO-TECH AI</p>
        </div>
      </div>

      {/* Recommandations */}
      {recos.length > 0 && (
        <Section icon={Sparkles} title="Vous pourriez également aimer">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {recos.map((c, i) => <MiniCard key={i} card={c} />)}
          </div>
        </Section>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <Section icon={ShoppingBag} title="Produits consultés" count={data?.viewed_products?.length || 0}>
          {data?.viewed_products?.length ? (
            <div className="space-y-2">{data.viewed_products.map((c, i) => <MiniCard key={i} card={c} />)}</div>
          ) : <p className="text-xs text-slate-400">Aucun produit consulté récemment.</p>}
        </Section>

        <Section icon={GraduationCap} title="Formations consultées" count={data?.viewed_formations?.length || 0}>
          {data?.viewed_formations?.length ? (
            <div className="space-y-2">{data.viewed_formations.map((c, i) => <MiniCard key={i} card={c} />)}</div>
          ) : <p className="text-xs text-slate-400">Aucune formation consultée récemment.</p>}
        </Section>

        <Section icon={Search} title="Recherches récentes" count={data?.searches?.length || 0}>
          {data?.searches?.length ? (
            <div className="flex flex-wrap gap-2">
              {data.searches.map((s, i) => (
                <span key={i} className="rounded-full border border-line bg-surface px-3 py-1 text-[12px] text-ink">{s.query}</span>
              ))}
            </div>
          ) : <p className="text-xs text-slate-400">Aucune recherche enregistrée.</p>}
        </Section>

        <Section icon={MessageSquare} title="Conversations avec l'IA" count={data?.conversations?.length || 0}>
          {data?.conversations?.length ? (
            <ul className="space-y-2">
              {data.conversations.map((c, i) => (
                <li key={i} className="flex items-center justify-between rounded-xl bg-surface px-3 py-2 text-[12.5px]">
                  <span className="text-ink">{c.count} message(s)</span>
                  <span className="text-[11px] text-slate-400">{formatDateTime(c.updated_at)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-xs text-slate-400">Aucune conversation. Ouvrez ISMO-TECH AI pour démarrer.</p>}
        </Section>
      </div>

      {/* Confidentialité */}
      <Section icon={ShieldCheck} title="Confidentialité et données">
        <p className="mb-4 text-[12.5px] leading-relaxed text-slate-500">
          Certaines données d'utilisation peuvent être utilisées pour améliorer le service, personnaliser les
          recommandations, fournir l'assistance et mesurer les performances du site. Nous n'utilisons jamais vos
          données pour déduire ou exposer des informations sensibles. Vous gardez le contrôle ci-dessous.
        </p>
        <div className="space-y-3">
          {PRIVACY_ITEMS.map((it) => (
            <div key={it.key} className="flex items-start justify-between gap-4 rounded-xl border border-line p-3">
              <div className="min-w-0">
                <p className="text-[13px] font-bold text-ink">{it.label}</p>
                <p className="text-[11.5px] text-slate-500">{it.desc}</p>
              </div>
              <Switch data-testid={`privacy-toggle-${it.key}`} checked={!!privacy[it.key]} onCheckedChange={() => togglePrivacy(it.key)} />
            </div>
          ))}
        </div>
        <button data-testid="account-clear-history" onClick={clearAll} className="mt-4 flex items-center gap-2 rounded-full border border-rose-200 px-4 py-2 text-[12.5px] font-bold text-rose-600 transition-colors hover:bg-rose-50">
          <Trash2 className="h-4 w-4" /> Effacer mon historique et mes conversations
        </button>
      </Section>
    </div>
  );
}
